import { DEFAULT_BOARD_ID, DEFAULT_ROSTER_ID, boards, rosters } from '@archon3d/content';
import {
  validateBoardDef,
  validateGameDefinition,
  validateRoster,
  type BoardDef,
  type FactionId,
  type GameState,
  type FighterId,
} from '@archon3d/core';
import { gameDefinition, useGameLibrary, refreshGameLibrary } from '../editor/gameLibrary';
import type { SavedGame } from '../game/save';
import { useCallback, useEffect, useRef, useState, useSyncExternalStore } from 'react';
import { PersistentAudioControls } from '../ui/RadioControls';
import { AudioBus } from '../audio/audioBus';
import { RadioPlayer } from '../audio/radio';
import { config } from '../config';
import { loadSavedBoards } from '../editor/boardStorage';
import { EditorStore } from '../editor/editorStore';
import { SceneHost, type SceneHostHandle } from '../engine/SceneHost';
import { BattleFeed } from '../game/battleFeed';
import { createWorkerBrain, type Brain } from '../game/brain';
import {
  Guide,
  loadGuideSeen,
  saveGuideSeen,
  type GuideHint,
  type GuideStorage,
} from '../game/guide';
import { createOnlineSession } from '../game/online';
import {
  canContinue,
  clearSavedGame,
  hasSavedGame,
  loadSavedGame,
  lostSaveText,
  saveGame,
} from '../game/save';
import { GameSession } from '../game/session';
import { useBattle } from '../game/useBattle';
import { pendingHandOver } from '../ui/handOver';
import { useSession } from '../game/useSession';
import { useRoom } from '../net/useRoom';
import { useServerStatus } from '../net/useServerStatus';
import {
  aiByFaction,
  defaultSetup,
  describeSetup,
  devicesByFaction,
  fitSetup,
  migrateSetup,
  namesByFaction,
  playingFactions,
  roomSetup,
  seatsByFaction,
  type GameSetup,
} from '../ui/gameSetup';
import { HomeScreen, type MenuPanel } from '../ui/menu/HomeScreen';
import { OnlineBanner } from '../ui/OnlineBanner';
import { ConnectionPaneOverlay } from '../ui/ConnectionPaneOverlay';
import { Overlay } from '../ui/Overlay';
import { DEFAULT_AI_DEPTH, SettingsStore, useSettings } from '../ui/settings';
import { ShellStore } from '../ui/shell';
import { failureReason } from '../ui/failure';
import { FailurePanel } from '../ui/FailurePanel';
import { reportError } from '../telemetry';
import { wasInVr, watchVrReturn } from './vrReturn';

const SETUP_KEY = 'archon3d.setup';
const BOARDS = [boards['hex-crown']!, boards[DEFAULT_BOARD_ID]!];
const CLASSIC = boards[DEFAULT_BOARD_ID]!;
const DEFAULT_GAME = CLASSIC;

/** The board an id names, among the bundled and the saved ones. */
function boardFor(boardId: string, saved: readonly BoardDef[]): BoardDef | null {
  return (
    gameDefinition(boardId)?.board ?? boards[boardId] ?? saved.find((b) => b.id === boardId) ?? null
  );
}

/**
 * Every new game is set up from the same place.
 *
 * This used to open on whatever was played last. It sounds helpful and is not: a player who sets
 * a game up perhaps once a sitting cannot remember what they left the panel on, so every start
 * meant reading all of it to find out. Fixed defaults are learnable — after two games you know
 * which one thing you change for the game you want — and they cost a tap when you want something
 * else. Anything genuinely worth keeping between visits is a setting, where it can be reviewed.
 *
 * The old stored setup is cleared on the way past so it does not sit in browsers for ever.
 */
function forgetStoredSetup(): void {
  try {
    localStorage.removeItem(SETUP_KEY);
  } catch {
    // Storage blocked: there was nothing kept in it to clear either.
  }
}

/** A game on the table, with what it was made from so it can be kept and brought back. */
interface TableGame {
  session: GameSession;
  setup: GameSetup;
  seed: number;
  board: BoardDef;
  online: boolean;
}

/** Where the lessons seen are kept between visits; none where the browser blocks storage. */
function guideStorage(): GuideStorage | null {
  try {
    return localStorage;
  } catch {
    return null;
  }
}

function newSeed(): number {
  return (Date.now() ^ Math.floor(Math.random() * 0xffffffff)) >>> 0;
}

function createLocalGame(
  setup: GameSetup,
  board: BoardDef,
  aiDepth: number,
  brain: Brain,
  seed = newSeed(),
  initialState?: GameState,
  definition = gameDefinition(board.id),
): TableGame {
  if (definition) {
    definition = structuredClone(validateGameDefinition(definition));
    if (JSON.stringify(board) !== JSON.stringify(definition.board))
      throw new Error('Game definition and board differ');
    board = definition.board;
  }
  const fitted = fitSetup(setup, board);
  const session = new GameSession({
    board,
    roster: definition ? validateRoster(definition.characters) : rosters[DEFAULT_ROSTER_ID]!,
    definition,
    seats: seatsByFaction(fitted, board),
    playing: playingFactions(fitted, board),
    seed,
    initialState,
    fights: fitted.fights,
    humanFriendlyAi: fitted.humanFriendlyAi,
    engagement: fitted.engagement,
    ai: aiByFaction(fitted, board),
    names: namesByFaction(fitted, board),
    devices: devicesByFaction(fitted, board),
    aiDepth,
    brain,
  });
  return { session, setup: fitted, seed, board, online: false };
}

/**
 * The game kept in this browser, back on the table; `null` when there is none to go on with, or
 * why it could not be (a line for the home screen) when there was one.
 */
function restoreGame(
  saved: readonly BoardDef[],
  aiDepth: number,
  brain: Brain,
): TableGame | string | null {
  const game = loadSavedGame();
  if (!game) {
    // Something is kept that this version cannot read at all: said once, then let go.
    if (!hasSavedGame()) return null;
    clearSavedGame();
    return lostSaveText(null);
  }
  if (!canContinue(game)) return null;
  const board = game.board ?? boardFor(game.setup.boardId, saved);
  if (!board) return null;
  try {
    const setup = migrateSetup(game.setup, board) ?? game.setup;
    return createLocalGame(setup, board, aiDepth, brain, game.seed, game.state, game.definition);
  } catch {
    // Kept by an older build and no longer readable: better a fresh game than a broken one, and a
    // word to say so rather than none (client-session:quality#10).
    clearSavedGame();
    return lostSaveText(game);
  }
}

export function App() {
  const catalogue = useGameLibrary();
  useEffect(() => {
    void refreshGameLibrary().catch(() => {});
  }, []);
  const [settingsStore] = useState(() => new SettingsStore());
  const settings = useSettings(settingsStore);
  // Boards from the editor live in this browser next to the bundled ones.
  const [savedBoards] = useState<BoardDef[]>(loadSavedBoards);
  const [editor] = useState(() => new EditorStore());
  const [setup, setSetup] = useState<GameSetup>(() => {
    forgetStoredSetup();
    return defaultSetup(DEFAULT_GAME, DEFAULT_AI_DEPTH);
  });
  // One worker for the whole visit: the AI thinks off the main thread, whatever the session.
  const [brain] = useState(() => createWorkerBrain());
  /** The last game comes back onto the table; the menu offers to continue it. */
  const [kept] = useState(() => restoreGame(savedBoards, DEFAULT_AI_DEPTH, brain));
  const restored = typeof kept === 'string' ? null : kept;
  /** Why a game kept in this browser did not come back, said once on the home screen. */
  const [lostSave, setLostSave] = useState(typeof kept === 'string' ? kept : null);
  const [game, setGame] = useState<TableGame>(
    () =>
      restored ??
      createLocalGame(
        setup,
        boardFor(setup.boardId, savedBoards) ?? CLASSIC,
        DEFAULT_AI_DEPTH,
        brain,
      ),
  );
  const session = game.session;
  const snapshot = useSession(session);
  const [feed] = useState(() => new BattleFeed());
  const battle = useBattle(feed);
  /** The guided first game: one lesson at a time, remembered across visits. */
  const [guide] = useState(() => new Guide(loadGuideSeen(guideStorage())));
  const [guideHint, setGuideHint] = useState<GuideHint | null>(null);
  // One bus for the visit; it stays silent until the first click, tap or key press unlocks it.
  const [audio] = useState(() => new AudioBus());
  useEffect(() => audio.attach(window), [audio]);
  const [radio] = useState(() => new RadioPlayer(audio));
  const [panel, setPanel] = useState<MenuPanel | null>(() =>
    new URLSearchParams(location.hash.slice(1)).has('account') ? 'account' : 'home',
  );
  /** A game is on behind the menu (started here, or brought back), so the menu can close to it. */
  const [playing, setPlaying] = useState(restored !== null);
  const playingRef = useRef(playing);
  playingRef.current = playing;
  useEffect(() => {
    if (playing) setLostSave(null);
  }, [playing]);
  /** A game brought back waits behind the menu until Continue, so the AI does not move unseen. */
  const [initialHold] = useState(() => (restored ? restored.session.hold() : null));
  const heldRef = useRef<(() => void) | null>(initialHold);
  const [shell] = useState(() => {
    const store = new ShellStore({
      setup,
      table: game.setup,
      playing: restored !== null,
      fog: settingsStore.get().fog,
      server: { state: 'checking' },
      xrSupported: null,
      guide: null,
      inXr: false,
      page: panel,
      version: `${config.version} · ${config.commit}`,
    });
    store.settings = settingsStore;
    store.radio = radio;
    return store;
  });
  const sceneRef = useRef<SceneHostHandle>(null);
  const [xrSupported, setXrSupported] = useState<boolean | null>(null);
  const shellState = useSyncExternalStore(
    (callback) => shell.subscribe(callback),
    () => shell.getSnapshot(),
  );
  /** The scene handle exists once the scene reports ready; settings are pushed from then on. */
  const [sceneReady, setSceneReady] = useState(false);
  const onSceneReady = useCallback((supported: boolean) => {
    setXrSupported(supported);
    setSceneReady(true);
  }, []);
  /** Why the table could not be built, while it cannot; the scene host is unmounted meanwhile. */
  const [sceneFailure, setSceneFailure] = useState<string | null>(null);
  const onSceneError = useCallback((error: unknown) => {
    reportError(error, 'The 3D table');
    setSceneReady(false);
    setSceneFailure(failureReason(error));
  }, []);
  const [xrError, setXrError] = useState<string | null>(null);
  /** The tab was in the headset when its page went: Home offers the way back (x-quest-vr#14). */
  const [vrReturn, setVrReturn] = useState(() => wasInVr());
  useEffect(() => watchVrReturn(shell), [shell]);
  const server = useServerStatus();

  useEffect(() => {
    window.__archon = {
      ...(window.__archon ?? { ready: false, activeMeshes: () => 0, fps: () => 0 }),
      session,
      audio,
      openPanel: setPanel,
    };
    return () => session.dispose();
  }, [session, audio]);
  useEffect(() => () => brain.dispose(), [brain]);

  // A local game is kept in this browser from its first move on, and forgotten when it ends.
  useEffect(() => {
    if (game.online) return;
    // Only a new rules state is written: a selection, a list of targets or the AI's thinking flag
    // had each stringified the whole game into storage again (client-tablets-ui:bugs#13). The
    // state the game starts watched in is the one it was restored from, or its opening, which is
    // not kept: nothing to write until the rules move on.
    let saved: GameState = game.session.getSnapshot().state;
    return game.session.subscribe(() => {
      const { state } = game.session.getSnapshot();
      if (state === saved) return;
      saved = state;
      if (state.phase === 'ended') clearSavedGame();
      else if (playingRef.current && state.turn > 0)
        saveGame({
          version: 1,
          setup: game.setup,
          seed: game.seed,
          state,
          board: game.board,
          definition: game.session.getSnapshot().ctx.definition,
          savedAt: Date.now(),
        });
    });
  }, [game]);

  // The guide watches the game on the table and raises each lesson the first time it comes up.
  useEffect(() => {
    const observe = () => {
      const hint = settings.guide ? guide.observe(game.session.getSnapshot()) : null;
      saveGuideSeen(guideStorage(), guide.seenSteps);
      setGuideHint((prev) => (prev?.step === hint?.step ? prev : hint));
    };
    observe();
    return game.session.subscribe(observe);
  }, [game, guide, settings.guide]);

  const release = () => {
    heldRef.current?.();
    heldRef.current = null;
  };

  // A hosted or joined game arrives from the server and goes onto the same table.
  const room = useRoom({
    serverUrl: config.serverUrl,
    onStarted: (started) => {
      const definition = started.started.setup.definition;
      const board = definition?.board ?? boards[started.started.setup.boardId] ?? CLASSIC;
      release();
      setGame({
        session: createOnlineSession(
          started,
          board,
          definition ? validateRoster(definition.characters) : rosters[DEFAULT_ROSTER_ID]!,
        ),
        setup: roomSetup(started.started, settings.fog),
        seed: started.started.seed,
        board,
        online: true,
      });
      setPanel(null);
      setPlaying(true);
    },
    // Shown where the player left it, unless they have gone on to something else meanwhile.
    onLobbyFound: (host) => setPanel((p) => (p === 'home' ? (host ? 'host' : 'join') : p)),
  });
  const leaveRoom = room.leave;

  const startGame = useCallback(
    (next: GameSetup) => {
      const board = boardFor(next.boardId, savedBoards) ?? CLASSIC;
      const fitted = fitSetup(next, board);
      setSetup(fitted);
      // The game's own choices become the table's until changed in the settings.
      settingsStore.set({ fog: fitted.fog });
      leaveRoom();
      release();
      clearSavedGame();
      setGame(createLocalGame(fitted, board, DEFAULT_AI_DEPTH, brain));
      setPanel(null);
      setPlaying(true);
    },
    [brain, savedBoards, settingsStore, leaveRoom, catalogue],
  );
  const leaveOnlineGame = useCallback(() => {
    leaveRoom();
    const found = restoreGame(savedBoards, DEFAULT_AI_DEPTH, brain);
    const back = typeof found === 'string' ? null : found;
    if (typeof found === 'string') setLostSave(found);
    heldRef.current = back ? back.session.hold() : null;
    setGame(
      back ??
        createLocalGame(
          setup,
          boardFor(setup.boardId, savedBoards) ?? CLASSIC,
          DEFAULT_AI_DEPTH,
          brain,
        ),
    );
    setPlaying(back !== null);
    setPanel('home');
  }, [leaveRoom, setup, savedBoards, settingsStore, brain]);
  /**
   * Quits the game on the table: the room is left, the save forgotten, and an idle board takes
   * its place behind the menu. Confirmed first — it is the one menu action that destroys
   * something, and until now the only way to abandon a game was to start another over it.
   */
  const quitGame = useCallback(() => {
    leaveRoom();
    release();
    clearSavedGame();
    setGame(
      createLocalGame(
        setup,
        boardFor(setup.boardId, savedBoards) ?? CLASSIC,
        DEFAULT_AI_DEPTH,
        brain,
      ),
    );
    setPlaying(false);
    setPanel('home');
  }, [leaveRoom, setup, savedBoards, settingsStore, brain]);
  const [accountLinkRevision, setAccountLinkRevision] = useState(0);
  useEffect(() => {
    const openAccountLink = () => {
      const link = new URLSearchParams(location.hash.slice(1));
      if (['verify', 'reset'].includes(link.get('account') ?? '') && link.has('token')) {
        setAccountLinkRevision((n) => n + 1);
        setPanel('account');
      }
    };
    window.addEventListener('hashchange', openAccountLink);
    return () => window.removeEventListener('hashchange', openAccountLink);
  }, []);
  const openMenu = useCallback(() => setPanel('home'), []);
  const openRules = useCallback(() => setPanel('rules'), []);
  const resumeGame = useCallback(() => {
    release();
    setPanel(null);
  }, []);
  /** Got it, on the screen or on the sign: the lesson goes until the board raises the next. */
  const dismissGuide = useCallback(() => {
    guide.dismiss();
    saveGuideSeen(guideStorage(), guide.seenSteps);
    setGuideHint(null);
  }, [guide]);
  /** The settings' way to take the lessons again, from the first. */
  const resetGuide = useCallback(() => {
    guide.reset();
    saveGuideSeen(guideStorage(), []);
    settingsStore.set({ guide: true });
    setGuideHint(guide.observe(game.session.getSnapshot()));
  }, [guide, game, settingsStore]);

  useEffect(() => {
    shell.set({
      setup,
      table: game.setup,
      playing,
      fog: settings.fog,
      server,
      xrSupported,
      guide: guideHint,
      page: panel,
    });
  }, [shell, setup, game.setup, playing, settings.fog, server, xrSupported, guideHint, panel]);
  // Opening a page must not let a local AI continue moving unseen behind it. An online game is not
  // held: the server plays on whatever one player has open, and a duel it starts has to reach
  // this player to pull them out of the menu (below); held, it waited behind the menu instead.
  useEffect(() => {
    if (panel === null || !playing || game.online) return;
    return session.hold();
  }, [session, panel, playing, game.online]);
  useEffect(() => {
    shell.settings = settingsStore;
    shell.radio = radio;
    shell.onNewGame = startGame;
    shell.onMenu = openMenu;
    shell.onQuitGame = quitGame;
    shell.onGuideDismiss = dismissGuide;
  }, [shell, startGame, settingsStore, radio, openMenu, quitGame, dismissGuide]);
  // The rest of the settings go straight to the scene and the sound bus.
  useEffect(() => {
    if (!sceneReady) return;
    sceneRef.current?.setFog(settings.fog);
    sceneRef.current?.setFogDensity(settings.fogDensity);
    sceneRef.current?.applyLook(settings);
    sceneRef.current?.setThreatHints(settings.threatHints);
  }, [
    sceneReady,
    settings.fog,
    settings.fogDensity,
    settings.graphics,
    settings.comfort,
    settings.palette,
    settings.tabletText,
    settings.threatHints,
  ]);
  useEffect(() => {
    audio.setVolume(settings.sound.volume);
    audio.setMuted(settings.sound.muted);
    radio.setMaster(settings.sound.volume, settings.sound.muted);
  }, [audio, radio, settings.sound]);
  useEffect(() => {
    const detach = radio.attach();
    return () => {
      detach();
      radio.dispose();
    };
  }, [radio]);
  // A move made in the headset is someone at the table too, and no page event says so there.
  useEffect(() => radio.touch(), [radio, snapshot.state.turn]);

  // A duel pulls the player into the arena whatever menu is open: online, the other side's
  // attack can start one at any time.
  const battlePhase = battle?.phase;
  useEffect(() => {
    if (battlePhase === 'intro') setPanel(null);
  }, [battlePhase]);

  // Esc opens the menu over a game and closes it again; a duel keeps the key for the mouse.
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== 'Escape' || battle) return;
      setPanel((current) => {
        if (current === null) return 'home';
        // The editor is work in progress, not a menu over the game: Esc leaves it open, where
        // its dialogs take the key (editor:quality#14).
        if (!playing || current === 'editor') return current;
        release();
        return null;
      });
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [battle, playing]);

  /**
   * People taking turns at one device: the moment acknowledged last, per game, so a new game
   * starts with the first person's hand-over again.
   */
  const [acknowledged, setAcknowledged] = useState<{
    game: TableGame;
    key: string;
    faction: FactionId;
  } | null>(null);
  const handOver =
    playing && !game.online
      ? pendingHandOver(
          snapshot,
          game.setup.oneDevice,
          acknowledged?.game === game ? acknowledged : null,
        )
      : null;
  const handOverReady = useCallback(() => {
    if (handOver) setAcknowledged({ game, key: handOver.key, faction: handOver.faction });
  }, [game, handOver]);
  // The Card chooser's clock waits while the device is on its way to the chooser, and while a menu
  // or the rule book is open over the game: fifteen seconds must not run out on a hand nobody sees.
  const pageOverGame = panel !== null && playing;
  useEffect(() => {
    session.holdCardClock(handOver?.reason === 'card' || pageOverGame);
  }, [session, handOver?.key, handOver?.reason, pageOverGame]);

  const forfeit = useCallback((side?: FighterId) => sceneRef.current?.forfeit(side), []);
  const continueOutcome = useCallback(() => sceneRef.current?.continueOutcome(), []);

  const enterVr = useCallback(async () => {
    setXrError(null);
    try {
      await sceneRef.current?.enterVr();
    } catch (err) {
      setXrError(err instanceof Error ? err.message : String(err));
    }
  }, []);

  /**
   * New game opens the panel on the defaults rather than on whatever is on the table, so the
   * choice a player has to make is the same one every time. Choosing a board or coming back from
   * the editor does not reset it: that is the middle of setting a game up, not the start of it.
   */
  const openSetup = useCallback(() => {
    setSetup(defaultSetup(DEFAULT_GAME, DEFAULT_AI_DEPTH));
    setPanel('new');
  }, [settingsStore]);
  /** A first game in one tap: the defaults, the person on the first side (x-player-journey#5). */
  const quickGame = useCallback(
    () => startGame(defaultSetup(DEFAULT_GAME, DEFAULT_AI_DEPTH)),
    [startGame, settingsStore],
  );

  // The turn as the sign counts it, from 1 (client-tablets-ui:quality#10).
  const resumeLabel = `${describeSetup(game.setup, game.board)} · turn ${snapshot.state.turn + 1}`;
  /**
   * A game at this table is over: the menu offers it again and New game rather than Continue and
   * a Quit that warns a finished game will be lost (x-player-journey#4). A room's rematch is the
   * room's to start, so online there is none.
   */
  const finished = playing && snapshot.state.phase === 'ended';
  const rematch = game.online ? undefined : () => startGame(game.setup);

  return (
    <>
      {sceneFailure === null && (
        <SceneHost
          ref={sceneRef}
          session={session}
          feed={feed}
          shell={shell}
          audio={audio}
          music={radio}
          onReady={onSceneReady}
          onError={onSceneError}
        />
      )}
      <div
        ref={(element) => {
          shell.screenElement = element;
        }}
        className={`screen-root${shellState.inXr && panel !== null ? ' floating-page-source' : ''}`}
      >
        <div className="persistent-controls">
          <PersistentAudioControls
            radio={radio}
            sound={settings.sound}
            onSoundChange={(sound) => settingsStore.set({ sound })}
          />
          {xrSupported === true && (
            <button
              className={`vr-toggle ${shellState.inXr ? 'active' : ''}`}
              aria-label={shellState.inXr ? 'Exit VR' : 'Enter VR'}
              aria-pressed={shellState.inXr}
              title={shellState.inXr ? 'Exit immersive VR' : 'Enter immersive VR'}
              onClick={() => {
                if (shellState.inXr)
                  void sceneRef.current?.exitVr().catch((err) => setXrError(String(err)));
                else void enterVr();
              }}
            >
              VR
            </button>
          )}
          {xrError && <span role="alert">{xrError}</span>}
        </div>
        {panel === null ? (
          <Overlay
            snapshot={snapshot}
            xrSupported={xrSupported}
            xrError={xrError}
            onEnterVr={() => void enterVr()}
            onMenu={openMenu}
            onRules={openRules}
            server={server}
            battle={battle}
            onForfeit={forfeit}
            onContinueOutcome={continueOutcome}
            guide={guideHint}
            onGuideDismiss={dismissGuide}
            radio={radio}
            sound={settings.sound}
            onSoundChange={(sound) => settingsStore.set({ sound })}
            onCardSupport={(card, role) => session.commitCardSupport(card, role)}
            onCardContinue={() => session.continueCardReveal()}
            onMuster={(pieceId) => session.respondToMuster(pieceId)}
            handOver={handOver}
            onHandOverReady={handOverReady}
            onRematch={rematch}
            onNewGame={openSetup}
            drawOfferable={session.canOfferDraw()}
            onOfferDraw={() => session.offerDraw()}
            onAnswerDraw={(accept) => session.answerDraw(accept)}
          />
        ) : (
          <HomeScreen
            key={accountLinkRevision}
            lostSave={playing ? null : lostSave}
            panel={panel}
            onPanel={setPanel}
            onNewGame={openSetup}
            onQuickGame={quickGame}
            boards={BOARDS}
            roster={snapshot.ctx.roster}
            savedBoards={savedBoards}
            cloudSaveBlocked={
              !playing || game.online
                ? 'Start or continue a local game first. Online games cannot be saved here.'
                : snapshot.state.phase === 'ended'
                  ? 'The game is over: there is nothing left to save.'
                  : snapshot.state.phase !== 'play'
                    ? 'Save between battles, while the board is in play.'
                    : null
            }
            cloudLoadBlocked={
              playing && snapshot.state.phase !== 'ended'
                ? 'Quit the current game before loading a cloud save.'
                : null
            }
            cloudGame={() => {
              if (!playing || game.online)
                throw new Error(
                  'Start or continue a local game first. Online games cannot be saved here.',
                );
              const state = game.session.getSnapshot().state;
              if (state.phase !== 'play')
                throw new Error('Save between battles, while the board is in play.');
              return {
                id: 'local-game',
                name: game.board.name,
                data: {
                  version: 1,
                  board: game.board,
                  game: {
                    version: 1,
                    setup: game.setup,
                    seed: game.seed,
                    state,
                    board: game.board,
                    definition: game.session.getSnapshot().ctx.definition,
                    savedAt: Date.now(),
                  },
                },
              };
            }}
            onCloudGame={(raw) => {
              // A finished game gives way to the one loaded; one in progress is quit first.
              if (playing && snapshot.state.phase !== 'ended')
                throw new Error('Quit the current game before loading a cloud save.');
              const saved = raw as { version: number; board: BoardDef; game: SavedGame };
              const board = validateBoardDef(saved.board);
              if (
                saved.version !== 1 ||
                saved.game.version !== 1 ||
                saved.game.state.phase !== 'play'
              )
                throw new Error('Unsupported game checkpoint');
              const restored = createLocalGame(
                saved.game.setup,
                board,
                DEFAULT_AI_DEPTH,
                brain,
                saved.game.seed,
                saved.game.state,
                saved.game.definition,
              );
              leaveRoom();
              heldRef.current = restored.session.hold();
              saveGame({ ...saved.game, board });
              setGame(restored);
              setPlaying(true);
              setPanel('home');
            }}
            editor={editor}
            setup={setup}
            onSetup={setSetup}
            onStart={() => startGame(setup)}
            canResume={playing && !finished}
            onRematch={finished ? rematch : undefined}
            resumeLabel={resumeLabel}
            onResume={resumeGame}
            onQuitGame={quitGame}
            onResetView={() => sceneRef.current?.resetView()}
            radio={radio}
            settings={settings}
            onSettings={(patch) => settingsStore.set(patch)}
            onResetSettings={() => settingsStore.reset()}
            onResetGuide={resetGuide}
            server={server}
            room={room}
            xrSupported={xrSupported}
            onEnterVr={() => void enterVr()}
            returnToVr={vrReturn && !shellState.inXr}
            onReturnToVr={() => {
              setVrReturn(false);
              void enterVr();
              if (playing && !finished) resumeGame();
            }}
            version={`${config.version} · ${config.commit}`}
          />
        )}
        {snapshot.online && (
          <OnlineBanner
            status={snapshot.online}
            onLeave={leaveOnlineGame}
            room={room}
            seatName={(seat) => {
              const faction = snapshot.ctx.board.def.factions[seat];
              return (
                (faction && snapshot.names[faction.id]) || faction?.name || `Player ${seat + 1}`
              );
            }}
          />
        )}
        {snapshot.online && panel === null && !room.replay && (
          <ConnectionPaneOverlay
            status={snapshot.online}
            finished={snapshot.state.phase === 'ended'}
            onReclaim={() => session.reclaimSeat()}
          />
        )}
      </div>
      {sceneFailure !== null && (
        <FailurePanel
          title="The 3D table could not be built"
          reason={sceneFailure}
          onRetry={() => setSceneFailure(null)}
        />
      )}
    </>
  );
}
